(() => {
  'use strict';
  if (customElements.get('sm-return-portal')) return;

  const API_ORIGIN = 'https://screenmate-retour-review.screenmate.chatgpt.site';
  const COPY = {
    stepNames: ['Bestelling vinden', 'Retour samenstellen', 'Controleren'],
    stepCount: 'Stap {step} van 3',
    lookupValidation: 'Vul een bestelnummer en een geldig e-mailadres in.',
    detailsValidation: 'Selecteer minimaal één product en controleer de retourreden, het mobiele nummer en het adres voor je label.',
    agreementValidation: 'Bevestig dat je gegevens kloppen en dat je akkoord gaat met de retourvoorwaarden.',
    requestError: 'Er ging iets mis. Probeer het opnieuw.',
    lookupBusy: 'Bestelling zoeken…',
    submitBusy: 'Aanvraag versturen…',
    orderDate: 'Besteld op {date}',
    productQuantity: 'Aantal {quantity}',
    originalPayment: 'Oorspronkelijke betaalmethode',
    storeCredit: 'Digitaal shoptegoed',
    demoTitle: 'Demo afgerond. Alles is doorlopen.',
    demoIntro: 'Dit was een demonstratie. Er is geen echte retouraanvraag aangemaakt en er is geen e-mail naar support of de klant verstuurd.',
    demoLabelTitle: 'In de echte omgeving volgt het label per e-mail',
    demoLabelMeta: 'Na controle sturen we het naar {email}',
    labelMeta: 'We sturen het naar {email}',
    fallbackImageAlt: 'Screenmate laptopscherm',
  };

  class ScreenmateReturnPortal extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.controller || !this.querySelector('#returnForm')) return;
        this.controller = new AbortController();
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
        this.motionAnimations = new Map();
        this.demo = this.dataset.demo === 'true' || new URLSearchParams(window.location.search).get('demo') === '1';
        this.apiBase = API_ORIGIN;
        try {
          const configured = new URL(this.dataset.apiBase || API_ORIGIN);
          if (configured.origin === API_ORIGIN && !configured.username && !configured.password) this.apiBase = configured.origin;
        } catch (_) {}
        this.copy = { ...COPY };
        try {
          const translations = JSON.parse(this.querySelector('[data-return-translations]')?.textContent || '{}');
          Object.keys(COPY).forEach((key) => {
            if (typeof translations[key] === 'string' || (key === 'stepNames' && Array.isArray(translations[key]) && translations[key].length === 3 && translations[key].every((value) => typeof value === 'string'))) this.copy[key] = translations[key];
          });
        } catch (_) {}
        this.currentStep = this.currentStep || 1;
        this.lookupToken = this.lookupToken || '';
        this.orderData = this.orderData || null;
        this.querySelectorAll('.field input:not([placeholder])').forEach((input) => { input.placeholder = ' '; });
        if (this.demo && !this.orderData) {
          this.el('demoBanner').hidden = false;
          this.el('orderNumber').value = 'DEMO-10001';
          this.el('email').value = 'demo@voorbeeld.nl';
        }
        this.on(this, 'click', (event) => {
          const next = event.target.closest('button[data-next]');
          const back = event.target.closest('button[data-back]');
          if (next && this.contains(next) && !next.disabled) this.next(next);
          if (back && this.contains(back) && !back.disabled) this.showStep(Number(back.dataset.back));
        });
        this.on(this.el('returnForm'), 'submit', (event) => {
          event.preventDefault();
          if (this.currentStep < 3) {
            const button = this.querySelector('.panel.active [data-next]');
            if (button && !button.disabled) this.next(button);
          } else this.submit();
        });
        this.on(this.el('returnForm'), 'input', (event) => event.target.removeAttribute('aria-invalid'));
        this.on(this.el('returnForm'), 'change', (event) => event.target.removeAttribute('aria-invalid'));
        this.setupMotion();
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.motionAnimations?.forEach((animation) => animation.cancel());
      this.motionAnimations?.clear();
    }

    el(id) { return this.querySelector(`#${id}`); }
    on(target, type, listener, options = {}) {
      target.addEventListener(type, listener, { ...options, signal: this.controller.signal });
    }
    text(key, replacements = {}) {
      return Object.entries(replacements).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, String(value)), this.copy[key]);
    }
    demoOrder() {
      return {
        name: 'DEMO-10001', processedAt: '2026-09-01T10:00:00.000Z', phone: '06 12345678',
        customer: { email: 'demo@voorbeeld.nl' },
        shippingAddress: { street: 'Voorbeeldstraat', houseNumber: '12', houseNumberAddition: 'A', postalCode: '1234 AB', city: 'Utrecht' },
        lineItems: [{ id: 'demo-item-1', name: 'Screenmate Triple', variantTitle: '15,6 inch', sku: 'SM-DEMO-001', quantity: 1, imageUrl: this.dataset.productImage || '', imageAlt: 'Screenmate Triple laptopscherm' }],
      };
    }

    showStep(step) {
      if (![1, 2, 3].includes(step)) return;
      this.currentStep = step;
      this.querySelectorAll('.panel').forEach((panel) => {
        const active = Number(panel.dataset.step) === step;
        panel.classList.toggle('active', active);
        panel.setAttribute('aria-hidden', String(!active));
      });
      this.querySelectorAll('.progress span').forEach((bar, index) => bar.classList.toggle('active', index < step));
      this.el('stepTitle').textContent = this.copy.stepNames[step - 1];
      this.el('stepCount').textContent = this.text('stepCount', { step });
      this.querySelectorAll('.step-navigation li').forEach((item, index) => {
        item.classList.toggle('current', index === step - 1);
        item.classList.toggle('completed', index < step - 1);
        if (index === step - 1) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
      this.focusHeading(this.el(`step${step}Heading`));
    }
    focusHeading(heading) {
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
      this.el('returnFlow').scrollIntoView({ behavior: this.reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    }
    showError(id, message, invalid = []) {
      const error = this.el(id);
      error.textContent = message;
      error.classList.add('show');
      invalid.forEach((input) => {
        input.setAttribute('aria-invalid', 'true');
        const descriptions = new Set((input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
        descriptions.add(id);
        input.setAttribute('aria-describedby', [...descriptions].join(' '));
      });
      if (invalid[0]) invalid[0].focus();
      else {
        error.setAttribute('tabindex', '-1');
        error.focus();
      }
    }
    clearError(id) {
      this.el(id).classList.remove('show');
      this.querySelectorAll(`[aria-describedby~="${id}"]`).forEach((input) => {
        input.removeAttribute('aria-invalid');
        const descriptions = input.getAttribute('aria-describedby').split(/\s+/).filter((value) => value !== id);
        if (descriptions.length) input.setAttribute('aria-describedby', descriptions.join(' '));
        else input.removeAttribute('aria-describedby');
      });
    }
    setBusy(button, key) {
      const content = button.querySelector('.button-content') || button;
      const original = [...content.childNodes];
      const spinner = document.createElement('span');
      spinner.className = 'spinner';
      spinner.setAttribute('aria-hidden', 'true');
      content.replaceChildren(spinner, document.createTextNode(this.text(key)));
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      return () => {
        content.replaceChildren(...original);
        button.disabled = false;
        button.removeAttribute('aria-busy');
      };
    }
    async postJson(path, body) {
      if (this.demo) throw new Error('Return requests are disabled in demo mode.');
      if (!['/api/returns/order', '/api/returns/create'].includes(path)) throw new Error(this.text('requestError'));
      const response = await fetch(new URL(path, this.apiBase), {
        method: 'POST', credentials: 'omit', cache: 'no-store',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify(body), signal: this.controller.signal,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || this.text('requestError'));
      return result;
    }
    imageUrl(value) {
      try {
        const url = new URL(value || this.dataset.productImage, window.location.origin);
        if (['https:', 'http:'].includes(url.protocol)) return url.href;
      } catch (_) {}
      return this.dataset.productImage || '';
    }
    renderOrder(order) {
      this.orderData = order;
      this.el('orderEcho').textContent = order.name;
      this.el('summaryOrder').textContent = order.name;
      this.el('orderDate').textContent = this.text('orderDate', { date: new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(order.processedAt)) });
      this.el('mobileNumber').value = order.phone || '';
      Object.entries({ returnStreet: 'street', returnHouseNumber: 'houseNumber', returnHouseAddition: 'houseNumberAddition', returnPostalCode: 'postalCode', returnCity: 'city' }).forEach(([id, key]) => { this.el(id).value = order.shippingAddress[key] || ''; });
      const container = this.el('orderItems');
      container.replaceChildren();
      order.lineItems.forEach((item, index) => {
        const label = document.createElement('label');
        label.className = 'product check';
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox'; checkbox.name = 'returnItem'; checkbox.value = item.id; checkbox.checked = index === 0;
        const checkUi = document.createElement('span');
        checkUi.className = 'checkbox-ui'; checkUi.setAttribute('aria-hidden', 'true');
        const imageWrap = document.createElement('span');
        imageWrap.className = 'product-image';
        const image = document.createElement('img');
        image.src = this.imageUrl(item.imageUrl); image.alt = item.imageAlt || item.name || this.text('fallbackImageAlt'); image.loading = 'lazy'; image.width = 74; image.height = 66;
        imageWrap.append(image);
        const info = document.createElement('span'); info.className = 'product-info';
        const title = document.createElement('strong'); title.textContent = item.name;
        const detail = document.createElement('span');
        detail.textContent = [item.variantTitle, item.sku ? `SKU ${item.sku}` : '', this.text('productQuantity', { quantity: item.quantity })].filter(Boolean).join(' · ');
        info.append(title, detail); label.append(checkbox, checkUi, imageWrap, info); container.append(label);
      });
    }
    selectedItems() { return [...this.querySelectorAll('input[name="returnItem"]:checked')].map((input) => input.value); }
    returnAddress() {
      return { street: this.el('returnStreet').value.trim(), houseNumber: this.el('returnHouseNumber').value.trim(), houseNumberAddition: this.el('returnHouseAddition').value.trim(), postalCode: this.el('returnPostalCode').value.trim().toUpperCase(), city: this.el('returnCity').value.trim() };
    }
    refundMethod() { return this.querySelector('input[name="refund"]:checked')?.value === 'Shoptegoed' ? 'credit' : 'refund'; }
    invalidDetails() {
      const invalid = [];
      if (!this.selectedItems().length) invalid.push(this.querySelector('input[name="returnItem"]'));
      if (!this.el('reason').value) invalid.push(this.el('reason'));
      let mobile = this.el('mobileNumber').value.trim().replace(/[\s()-]/g, '');
      if (mobile.startsWith('06')) mobile = `+31${mobile.slice(1)}`;
      if (mobile.startsWith('0031')) mobile = `+${mobile.slice(2)}`;
      if (!/^\+[1-9]\d{7,14}$/.test(mobile)) invalid.push(this.el('mobileNumber'));
      const address = this.returnAddress();
      if (address.street.length < 2) invalid.push(this.el('returnStreet'));
      if (!/^\d+[A-Za-z0-9-]*$/.test(address.houseNumber)) invalid.push(this.el('returnHouseNumber'));
      if (!/^\d{4}\s?[A-Z]{2}$/.test(address.postalCode)) invalid.push(this.el('returnPostalCode'));
      if (address.city.length < 2) invalid.push(this.el('returnCity'));
      return invalid.filter(Boolean);
    }
    async next(button) {
      if (this.currentStep === 1) {
        const number = this.el('orderNumber'); const email = this.el('email');
        const invalid = [];
        if (number.value.trim().length < 3) invalid.push(number);
        if (!email.validity.valid || !email.value.trim()) invalid.push(email);
        if (invalid.length) { this.showError('step1Error', this.text('lookupValidation'), invalid); return; }
        this.clearError('step1Error');
        const restore = this.setBusy(button, 'lookupBusy');
        try {
          const result = this.demo ? { token: 'demo-lookup-token', order: this.demoOrder() } : await this.postJson('/api/returns/order', { orderNumber: number.value.trim(), email: email.value.trim() });
          if (!this.isConnected) return;
          this.lookupToken = result.token;
          this.renderOrder(result.order);
          this.showStep(2);
        } catch (error) {
          if (error.name !== 'AbortError' && this.isConnected) this.showError('step1Error', error.message || this.text('requestError'));
        } finally { restore(); }
        return;
      }
      if (this.currentStep !== 2 || !this.orderData) return;
      const invalid = this.invalidDetails();
      if (invalid.length) { this.showError('step2Error', this.text('detailsValidation'), invalid); return; }
      this.clearError('step2Error');
      const ids = new Set(this.selectedItems());
      this.el('summaryProducts').textContent = this.orderData.lineItems.filter((item) => ids.has(item.id)).map((item) => `${item.name} · ${item.quantity}×`).join(', ');
      this.el('summaryReason').textContent = this.el('reason').value;
      const address = this.returnAddress();
      this.el('summaryAddress').textContent = `${address.street} ${address.houseNumber}${address.houseNumberAddition ? ` ${address.houseNumberAddition}` : ''}, ${address.postalCode} ${address.city}`;
      this.el('summaryRefund').textContent = this.text(this.refundMethod() === 'credit' ? 'storeCredit' : 'originalPayment');
      this.showStep(3);
    }
    async submit() {
      const button = this.el('submitReturn');
      if (button.disabled || !this.orderData || !this.lookupToken) return;
      if (!this.el('agree').checked) { this.showError('step3Error', this.text('agreementValidation'), [this.el('agree')]); return; }
      this.clearError('step3Error');
      const restore = this.setBusy(button, 'submitBusy');
      try {
        const result = this.demo ? { rmaReference: 'DEMO-RMA-10001' } : await this.postJson('/api/returns/create', { token: this.lookupToken, itemIds: this.selectedItems(), reason: this.el('reason').value, comment: this.el('comment').value, refundMethod: this.refundMethod(), mobileNumber: this.el('mobileNumber').value, returnAddress: this.returnAddress() });
        if (!this.isConnected) return;
        this.el('returnForm').hidden = true;
        this.el('progressWrap').hidden = true;
        this.el('successRma').textContent = result.rmaReference;
        if (this.demo) {
          this.el('successTitle').textContent = this.text('demoTitle');
          this.el('successIntro').textContent = this.text('demoIntro');
          this.el('labelTitle').textContent = this.text('demoLabelTitle');
        }
        this.el('labelMeta').textContent = this.text(this.demo ? 'demoLabelMeta' : 'labelMeta', { email: this.orderData.customer.email });
        this.el('success').classList.add('active');
        this.focusHeading(this.el('successTitle'));
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) this.showError('step3Error', error.message || this.text('requestError'));
      } finally { restore(); }
    }

    setupMotion() {
      const controls = [...this.querySelectorAll('.primary')];
      const reset = (button) => { button.style.setProperty('--magnet-x', '0px'); button.style.setProperty('--magnet-y', '0px'); };
      const hover = (button, active) => {
        if (button.disabled) return;
        const fill = button.querySelector('.button-fill');
        const previous = this.motionAnimations.get(button);
        const from = active && previous?.playState !== 'running' ? 'translateY(76%)' : getComputedStyle(fill).transform;
        previous?.cancel();
        button.classList.toggle('is-hovered', active);
        if (this.reducedMotion.matches) return;
        this.motionAnimations.set(button, fill.animate([{ transform: from }, { transform: active ? 'translateY(0)' : 'translateY(-76%)' }], { duration: 600, easing: 'cubic-bezier(.3,1,.3,1)' }));
      };
      controls.forEach((button) => {
        if (!button.querySelector('.button-content')) {
          const content = document.createElement('span'); content.className = 'button-content'; content.append(...button.childNodes);
          const fill = document.createElement('span'); fill.className = 'button-fill'; fill.setAttribute('aria-hidden', 'true');
          button.append(fill, content);
        }
        button.classList.add('motion-button');
        this.on(button, 'pointerenter', () => { if (this.finePointer.matches) hover(button, true); });
        this.on(button, 'pointerleave', () => { hover(button, button.matches(':focus-visible')); reset(button); });
        this.on(button, 'focus', () => { if (button.matches(':focus-visible')) hover(button, true); });
        this.on(button, 'blur', () => { hover(button, this.finePointer.matches && button.matches(':hover')); reset(button); });
        this.on(button, 'pointermove', (event) => {
          if (!this.finePointer.matches || this.reducedMotion.matches || button.disabled) return;
          const box = button.getBoundingClientRect();
          button.style.setProperty('--magnet-x', `${((event.clientX - box.left) / box.width - .5) * 10}px`);
          button.style.setProperty('--magnet-y', `${((event.clientY - box.top) / box.height - .5) * 10}px`);
        });
      });
      this.on(this.reducedMotion, 'change', () => {
        controls.forEach(reset);
        if (this.reducedMotion.matches) this.motionAnimations.forEach((animation) => animation.cancel());
      });
      this.on(this.finePointer, 'change', () => controls.forEach(reset));
    }
  }
  customElements.define('sm-return-portal', ScreenmateReturnPortal);
})();
